/**
 * Topic Sharing Service
 * Handles sharing Topics via shareable links (mirrors spaceSharingService / threadSharingService)
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { TopicShare, TopicShareAccess, Topic } from "@shared/schema";
import { spaceSharingService } from "@/services/spaceSharingService";

export interface CreateTopicShareLinkParams {
  topicId: string;
  accessType?: "restricted" | "anyone" | "organization";
  expiresAt?: Date | null;
}

export interface TopicShareLinkValidationResult {
  isValid: boolean;
  share: TopicShare | null;
  topic: Topic | null;
  access: TopicShareAccess | null;
  message: string;
}

class TopicSharingService {
  private generateShareToken(): string {
    const randomBytes = new Uint8Array(24);
    crypto.getRandomValues(randomBytes);
    const base64 = btoa(String.fromCharCode(...randomBytes));
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").substring(0, 32);
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated");
    }
    return session.user.id;
  }

  private mapToTopicShare(item: any): TopicShare {
    return {
      id: item.id,
      topicId: item.topic_id,
      createdByUserId: item.created_by_user_id,
      shareToken: item.share_token,
      accessType: item.access_type,
      expiresAt: item.expires_at,
      isActive: item.is_active === "true",
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }

  private mapToTopicShareAccess(item: any): TopicShareAccess {
    return {
      id: item.id,
      shareId: item.share_id,
      userId: item.user_id,
      status: item.status,
      accessedAt: item.accessed_at,
      respondedAt: item.responded_at,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }

  private mapRowToTopic(row: any): Topic {
    return {
      id: String(row.id),
      spaceId: row.space_id,
      name: row.name,
      description: row.description,
      color: row.color,
      order: row.order || "0",
      isLocked: row.is_locked === true || row.is_locked === "true",
      lockedAt: row.locked_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    } as Topic;
  }

  async createShareLink(params: CreateTopicShareLinkParams): Promise<TopicShare> {
    const userId = await this.getCurrentUserId();

    const { data: topicRow, error: topicError } = await supabase
      .from("topics")
      .select("id, space_id, name")
      .eq("id", params.topicId)
      .single();

    if (topicError || !topicRow) {
      throw new Error("Topic not found");
    }

    // Permission: user must have access to the space that owns this topic
    // (owner OR accepted shared member)
    const hasAccess = await spaceSharingService.hasSpaceAccess(topicRow.space_id);
    if (!hasAccess) {
      throw new Error("Topic not found or you don't have permission to share it");
    }

    const { data: existingShare } = await supabase
      .from("topic_shares")
      .select("id, topic_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("topic_id", params.topicId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .single();

    if (existingShare) {
      const updateData: any = { updated_at: new Date().toISOString() };
      if (params.accessType) updateData.access_type = params.accessType;
      if (params.expiresAt !== undefined) updateData.expires_at = params.expiresAt?.toISOString() || null;

      const { data: updatedShare, error: updateError } = await supabase
        .from("topic_shares")
        .update(updateData)
        .eq("id", existingShare.id)
        .select()
        .single();

      if (updateError) throw new Error(`Failed to update share link: ${updateError.message}`);
      return this.mapToTopicShare(updatedShare);
    }

    const shareToken = this.generateShareToken();
    const shareData: any = {
      topic_id: params.topicId,
      created_by_user_id: userId,
      share_token: shareToken,
      access_type: params.accessType || "restricted",
      expires_at: params.expiresAt?.toISOString() || null,
      is_active: "true",
    };

    const { data: newShare, error: createError } = await supabase
      .from("topic_shares")
      .insert(shareData)
      .select()
      .single();

    if (createError) throw new Error(`Failed to create share link: ${createError.message}`);
    return this.mapToTopicShare(newShare);
  }

  async validateShareLink(token: string): Promise<TopicShareLinkValidationResult> {
    const { data: share, error: shareError } = await supabase
      .from("topic_shares")
      .select("id, topic_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) {
      return {
        isValid: false,
        share: null,
        topic: null,
        access: null,
        message: "Link not valid or has been deactivated",
      };
    }

    if (share.expires_at) {
      const expiresAt = new Date(share.expires_at);
      if (expiresAt < new Date()) {
        return {
          isValid: false,
          share: this.mapToTopicShare(share),
          topic: null,
          access: null,
          message: "This share link has expired",
        };
      }
    }

    const { data: topicRow, error: topicError } = await supabase
      .from("topics")
      .select("id, space_id, name, description, color, order, is_locked, locked_at, created_at, updated_at")
      .eq("id", share.topic_id)
      .single();

    if (topicError || !topicRow) {
      return {
        isValid: false,
        share: this.mapToTopicShare(share),
        topic: null,
        access: null,
        message: "Topic not found or has been deleted",
      };
    }

    let access: TopicShareAccess | null = null;
    try {
      const userId = await this.getCurrentUserId();
      const { data: accessRecord } = await supabase
        .from("topic_share_access")
        .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
        .eq("share_id", share.id)
        .eq("user_id", userId)
        .single();

      if (accessRecord) access = this.mapToTopicShareAccess(accessRecord);
    } catch {
      // User not authenticated
    }

    return {
      isValid: true,
      share: this.mapToTopicShare(share),
      topic: this.mapRowToTopic(topicRow),
      access,
      message: "Link is valid",
    };
  }

  async acceptShareInvitation(token: string): Promise<TopicShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("topic_shares")
      .select("id, topic_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) throw new Error("Share link not found or invalid");

    const { data: existingAccess } = await supabase
      .from("topic_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      if (existingAccess.status !== "accepted") {
        const { data: updatedAccess, error: updateError } = await supabase
          .from("topic_share_access")
          .update({
            status: "accepted",
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingAccess.id)
          .select()
          .single();
        if (updateError) throw new Error(`Failed to accept invitation: ${updateError.message}`);
        return this.mapToTopicShareAccess(updatedAccess);
      }
      return this.mapToTopicShareAccess(existingAccess);
    }

    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "accepted",
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("topic_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) throw new Error(`Failed to accept invitation: ${createError.message}`);
    return this.mapToTopicShareAccess(newAccess);
  }

  async rejectShareInvitation(token: string): Promise<TopicShareAccess> {
    const userId = await this.getCurrentUserId();

    const { data: share, error: shareError } = await supabase
      .from("topic_shares")
      .select("id, topic_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("share_token", token)
      .eq("is_active", "true")
      .single();

    if (shareError || !share) throw new Error("Share link not found or invalid");

    const { data: existingAccess } = await supabase
      .from("topic_share_access")
      .select("id, share_id, user_id, status, accessed_at, responded_at, created_at, updated_at")
      .eq("share_id", share.id)
      .eq("user_id", userId)
      .single();

    if (existingAccess) {
      const { data: updatedAccess, error: updateError } = await supabase
        .from("topic_share_access")
        .update({
          status: "rejected",
          responded_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", existingAccess.id)
        .select()
        .single();
      if (updateError) throw new Error(`Failed to reject invitation: ${updateError.message}`);
      return this.mapToTopicShareAccess(updatedAccess);
    }

    const accessData: any = {
      share_id: share.id,
      user_id: userId,
      status: "rejected",
      accessed_at: new Date().toISOString(),
      responded_at: new Date().toISOString(),
    };

    const { data: newAccess, error: createError } = await supabase
      .from("topic_share_access")
      .insert(accessData)
      .select()
      .single();

    if (createError) throw new Error(`Failed to reject invitation: ${createError.message}`);
    return this.mapToTopicShareAccess(newAccess);
  }

  async revokeShareLink(topicId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("topic_shares")
      .update({
        is_active: "false",
        updated_at: new Date().toISOString(),
      })
      .eq("topic_id", topicId)
      .eq("created_by_user_id", userId);

    if (error) throw new Error(`Failed to revoke share link: ${error.message}`);
    return true;
  }

  async getShareLink(topicId: string): Promise<TopicShare | null> {
    const userId = await this.getCurrentUserId();

    const { data: share, error } = await supabase
      .from("topic_shares")
      .select("id, topic_id, created_by_user_id, share_token, access_type, expires_at, is_active, created_at, updated_at")
      .eq("topic_id", topicId)
      .eq("created_by_user_id", userId)
      .eq("is_active", "true")
      .single();

    if (error || !share) return null;
    return this.mapToTopicShare(share);
  }
}

export const topicSharingService = new TopicSharingService();
